import React, { useEffect, useState } from "react";
import { Loader2, CheckCircle2, XCircle, GraduationCap, PartyPopper } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

export function QuizDialog({ open, onOpenChange, onDone }) {
  const { t } = useI18n();
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [result, setResult] = useState(null);
  const [correctCount, setCorrectCount] = useState(0);
  const [finished, setFinished] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setQuestions([]); setIdx(0); setSelected(null); setResult(null); setCorrectCount(0); setFinished(false);
    api.get("/learning/quiz/daily")
      .then(({ data }) => setQuestions(data.questions || []))
      .catch(() => setQuestions([]))
      .finally(() => setLoading(false));
  }, [open]);

  const q = questions[idx];

  const answer = async (opt) => {
    if (selected != null) return;
    setSelected(opt);
    try {
      const { data } = await api.post("/learning/quiz/attempt", { phrase_id: q.phrase_id, question_type: q.question_type, answer: opt });
      setResult(data);
      if (data.correct) setCorrectCount((c) => c + 1);
    } catch { setResult({ correct: false, correct_answer: "" }); }
  };

  const next = () => {
    if (idx + 1 >= questions.length) {
      setFinished(true);
      onDone && onDone();
    } else {
      setIdx((i) => i + 1); setSelected(null); setResult(null);
    }
  };

  const optClass = (opt) => {
    if (selected == null) return "border-border hover:border-accent/50 hover:bg-secondary/50";
    if (opt === result?.correct_answer) return "border-accent bg-accent/10 text-accent";
    if (opt === selected) return "border-destructive bg-destructive/10 text-destructive";
    return "border-border opacity-60";
  };

  const isAr = q?.question_type === "en-to-ar";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="quiz-dialog">
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2"><GraduationCap size={18} className="text-accent" /> {t("dailyQuiz")}</DialogTitle>
          <DialogDescription>{finished ? t("quizComplete") : loading ? "…" : questions.length ? `${t("quizQuestionOf")} ${idx + 1}/${questions.length}` : t("quizEmpty")}</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="grid place-items-center py-10 text-primary"><Loader2 className="animate-spin" /></div>
        ) : finished ? (
          <div className="text-center py-6" data-testid="quiz-summary">
            <div className="h-14 w-14 rounded-2xl bg-accent/10 text-accent grid place-items-center mx-auto mb-3"><PartyPopper size={26} /></div>
            <div className="font-head font-bold text-3xl text-foreground">{correctCount}/{questions.length}</div>
            <div className="text-sm text-muted-foreground mt-1">{t("quizYouScored")}</div>
            <button onClick={() => onOpenChange(false)} data-testid="quiz-close-btn" className="mt-5 h-11 px-6 rounded-xl bg-accent text-white font-medium hover:bg-accent/90">{t("quizDone")}</button>
          </div>
        ) : questions.length === 0 ? (
          <div className="text-center text-sm text-muted-foreground py-8">{t("quizEmpty")}</div>
        ) : q ? (
          <div>
            <Progress value={((idx) / questions.length) * 100} className="h-1.5 mb-4" />
            <div className="text-xs text-muted-foreground mb-1">{isAr ? "EN → ع" : "ع → EN"}</div>
            <div className="text-xl font-semibold text-foreground mb-4" dir={isAr ? "ltr" : "rtl"} data-testid="quiz-prompt">{q.prompt}</div>
            <div className="space-y-2">
              {q.options.map((opt, i) => (
                <button
                  key={opt}
                  onClick={() => answer(opt)}
                  disabled={selected != null}
                  data-testid={`quiz-option-${i}`}
                  className={`w-full text-start px-4 py-3 rounded-xl border-2 transition-colors flex items-center justify-between gap-2 ${optClass(opt)}`}
                  dir={isAr ? "rtl" : "ltr"}
                >
                  <span>{opt}</span>
                  {selected != null && opt === result?.correct_answer && <CheckCircle2 size={17} />}
                  {selected != null && opt === selected && opt !== result?.correct_answer && <XCircle size={17} />}
                </button>
              ))}
            </div>
            {selected != null && (
              <div className="flex items-center justify-between mt-4" data-testid="quiz-feedback">
                <span className={`text-sm font-medium ${result?.correct ? "text-accent" : "text-destructive"}`}>
                  {result?.correct ? t("quizCorrect") : t("quizWrong")}
                </span>
                <button onClick={next} data-testid="quiz-next-btn" className="h-10 px-5 rounded-xl bg-primary text-white font-medium hover:bg-primary/90">{t("quizNext")}</button>
              </div>
            )}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
